import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { ReceiptScope } from './receipt-scope.js';
import { OfflineReceiptAction, OfflineReceiptActionType } from './offline-receipt-action.js';
import { OfflineReceipt } from './offline-receipt.js';
import { LegacyReceipt } from './legacy-receipt.js';
import { LegacyReceiptGroup } from './legacy-receipt-group.js';
import { SerializationException } from '../../../exceptions.js';

function convertLegacyReceiptsToActions(
    currentReceipt: LegacyReceipt | null,
    pastReceipts: LegacyReceipt[],
): OfflineReceiptAction[] {
  const actions: OfflineReceiptAction[] = [];

  for(const pastReceipt of pastReceipts) {
    actions.push(OfflineReceiptAction.asCreate(OfflineReceipt.fromLegacyAsActive(pastReceipt)));

    const cancelledReceipt = OfflineReceipt.fromLegacyAsCancelled(pastReceipt);
    if(cancelledReceipt == null) {
      // past receipt is guaranteed (by the Gods) to be cancelled!
      throw new SerializationException('Past receipt is not cancelled. This should not happen.');
    }

    // legacy receipts contain both creation and cancellation data in a single instance unlike the action based system
    // what this means is that for every "past" receipt we must generate two actions(create & cancel)
    actions.push(OfflineReceiptAction.asCancel(cancelledReceipt));
  }

  if(currentReceipt != null) {
    if(currentReceipt.isCancelled) {
      // current receipt is guaranteed (by the Gods) to NOT be cancelled!
      throw new SerializationException('Current receipt is cancelled. This should not happen.');
    }

    const receipt = OfflineReceipt.fromLegacyAsActive(currentReceipt);
    actions.push(OfflineReceiptAction.asCreate(receipt));
  }

  return actions;
}

export class OfflineReceiptGroup extends DataModel {
  static readonly TYPE_ID: string = '81bc9ae62125404cbfe4f43688cc3812';
  static readonly SCHEMA_VERSION: number = 1;

  private constructor(
    public readonly uniqueId: string,
    public readonly scope: ReceiptScope,
    private readonly actions: OfflineReceiptAction[],
  ) {
    super();
  }

  get $typeId() {
    return OfflineReceiptGroup.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OfflineReceiptGroup.SCHEMA_VERSION);
  }

  static scopeToUniqueId(scope: ReceiptScope): string {
    return fromEnumAsString(scope).toLowerCase();
  }

  static emptyGeneric(uniqueId: string): OfflineReceiptGroup {
    return new OfflineReceiptGroup(
        uniqueId,
        ReceiptScope.Generic,
        [],
    );
  }

  static emptyLegacy(): OfflineReceiptGroup {
    return new OfflineReceiptGroup(
        this.scopeToUniqueId(ReceiptScope.Order),
        ReceiptScope.Order,
        [],
    );
  }

  static create(uniqueId: string, receipt: OfflineReceipt, scope: ReceiptScope): OfflineReceiptGroup {
    return new OfflineReceiptGroup(
        uniqueId,
        scope,
        [OfflineReceiptAction.asCreate(receipt)],
    );
  }

  static fromLegacyReceipts(currentReceipt: LegacyReceipt | null, pastReceipts: LegacyReceipt[]): OfflineReceiptGroup {
    const actions = convertLegacyReceiptsToActions(currentReceipt, pastReceipts);
    return new OfflineReceiptGroup(
        this.scopeToUniqueId(ReceiptScope.Order),
        ReceiptScope.Order,
        actions,
    );
  }

  static fromLegacyGroup(legacyGroup: LegacyReceiptGroup): OfflineReceiptGroup {
    const actions = convertLegacyReceiptsToActions(legacyGroup.getActiveReceipt(), legacyGroup.getPastReceipts());
    return new OfflineReceiptGroup(
        legacyGroup.uniqueId,
        legacyGroup.scope,
        actions,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineReceiptGroup {
    return new OfflineReceiptGroup(
        MapUtils.getString(map, 'uniqueId'),
        toEnumFromString(MapUtils.getString(map, 'scope'), ReceiptScope),
        MapUtils.getList<OfflineReceiptAction>(map, 'actions', (value) => {
          return context.fromData<OfflineReceiptAction>(value as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'scope': fromEnumAsString(this.scope),
      'actions': this.actions.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new OfflineReceiptGroup(
        this.uniqueId,
        this.scope,
        this.actions.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: OfflineReceiptGroup): boolean {
    if(this === other) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
      this.scope === other.scope &&
      isSameList(other.actions, this.actions, isSameData);
  }

  getTotalNumberOfCreatedReceipts(): number {
    return this.actions.reduce<number>((sum, act) => sum + (act.type === OfflineReceiptActionType.Create ? act.numberOfReceipts : 0), 0);
  }

  hasAnyReceipt(): boolean {
    return this.actions.length > 0;
  }

  hasActiveReceipt(): boolean {
    if(this.actions.length <= 0) return false;

    const lastAction = this.actions[this.actions.length - 1];
    return lastAction.type === OfflineReceiptActionType.Create;
  }

  private tryFindActiveAction(): OfflineReceiptAction | null {
    if(this.actions.length <= 0) return null;

    const activeAction = this.actions[this.actions.length - 1];
    if(activeAction.type != OfflineReceiptActionType.Create) return null;

    return activeAction;
  }

  tryFindActiveReceipt(): OfflineReceipt | null {
    const activeAction = this.tryFindActiveAction();
    if(activeAction == null) {
      return null;
    }
    if(activeAction.numberOfReceipts <= 0 || activeAction.numberOfReceipts > 1) {
      return null;
    }

    return activeAction.getReceipt(0);
  }

  addAction(action: OfflineReceiptAction): void {
    this.actions.push(action);
  }

  getReceiptsForExport(): OfflineReceipt[] {
    const receipts: OfflineReceipt[] = [];
    for(const action of this.actions) {
      receipts.push(...action.getAllReceipts());
    }

    return receipts;
  }

  getReceiptsForCheckout(): OfflineReceipt[] {
    const activeAction = this.tryFindActiveAction();
    if(activeAction == null) return [];

    return [...activeAction.getReceiptsForCheckout()];
  }

  toDiagnostics(): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'scope': fromEnumAsString(this.scope),
      'actions': this.actions.map((e) => e.toDiagnostics()),
    };
  }
}

export class OfflineReceiptGroupBuilder extends DataModelBuilder<OfflineReceiptGroup> {
  build(context: SerializationContext, map: SerializedData): OfflineReceiptGroup {
    return OfflineReceiptGroup.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OfflineReceiptGroup.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OfflineReceiptGroup.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}

